import {
  Box,
  Button,
  HStack,
  Link,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react'
import type React from 'react'
import KycStatusBox from '../../../../../../components/KycStatus'
import { useUpdateKycStatusMutation } from '../../../../../../redux/services/coreApi'
import type { KycStatus, UserDto } from '../../../../../../types/coreApi-types'

interface KYCProps {
  client?: UserDto
}

const KYC: React.FC<KYCProps> = ({ client }) => {
  const isOrganisation = client?.role === 'Organization'
  const toast = useToast()
  const [updateKycStatus, { isLoading: isUpdatingKycStatus }] =
    useUpdateKycStatusMutation()

  if (!client) {
    return (
      <div className="flex w-full h-full justify-center items-center">
        <Spinner size="lg" />
      </div>
    )
  }

  const handleUpdateKycStatus = async (kycStatus: KycStatus) => {
    if (!client.id) return

    try {
      await updateKycStatus({
        userId: client.id,
        kycStatus,
      }).unwrap()

      toast({
        title: 'Success',
        description: `${isOrganisation ? 'KYB' : 'KYC'} status updated successfully`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to update ${isOrganisation ? 'KYB' : 'KYC'} status`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right',
      })
    }
  }

  return (
    <VStack spacing={6} align="stretch" w="full">
      <Box>
        <Text fontSize="2xl" fontWeight="bold" mb={4}>
          KYC/KYB Management
        </Text>
      </Box>

      {/* Main Status Section */}
      <Box className="card" p={6}>
        <VStack spacing={4} align="stretch">
          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="semibold">
              {client.role === 'Organization' ? 'KYB' : 'KYC'} Status
            </Text>
            {client.kycDetails?.kycStatus && (
              <KycStatusBox kycStatus={client.kycDetails.kycStatus} />
            )}
          </HStack>
        </VStack>
      </Box>

      {/* KYC/KYB Admin Action Buttons */}
      <Box className="card" p={6}>
        <Text fontSize="md" fontWeight="medium" mb={2}>
          Update {client.role === 'Organization' ? 'KYB' : 'KYC'} Status
        </Text>
        <HStack spacing={4}>
          <Button
            colorScheme="green"
            isLoading={isUpdatingKycStatus}
            onClick={() => handleUpdateKycStatus('Approved')}
            isDisabled={client.kycDetails?.kycStatus === 'Approved'}
          >
            Approve
          </Button>
          <Button
            colorScheme="red"
            isLoading={isUpdatingKycStatus}
            onClick={() => handleUpdateKycStatus('Declined')}
            isDisabled={client.kycDetails?.kycStatus === 'Declined'}
          >
            Decline
          </Button>
        </HStack>
      </Box>

      {/* Sumsub Section */}
      {client.kycDetails?.sumsubApplicantId && (
        <Box className="card" p={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="semibold">
              Sumsub Integration
            </Text>
            <Link
              href={`https://cockpit.sumsub.com/checkus#/applicant/${client.kycDetails.sumsubApplicantId}/basicInfo`}
              isExternal
              color="blue.500"
              _hover={{ color: 'blue.600' }}
            >
              View in Sumsub Dashboard →
            </Link>
          </VStack>
        </Box>
      )}
    </VStack>
  )
}

export default KYC
